import React, {useState, useRef} from 'react';
import {Box, VStack, Text, useTheme, HStack} from '@chakra-ui/react';
import Icon from '../../components/Icon';
import GooglePlacesAutocomplete from '../../components/GooglePlacesAutocomplete';
import {TextG14, TextG16, TextG24} from '../../components/typography';
import logger from '../../utils/logger';

export interface AddressData {
  formatted_address: string;
  place: any; // Google Places API place object with address_components
}

interface IntroAddressProps {
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (address: AddressData | null) => void;
  initialAddress?: AddressData;
}

const IntroAddress = ({onValidationChange, onDataChange, initialAddress}: IntroAddressProps) => {
  const theme = useTheme();
  const [address, setAddress] = useState(initialAddress?.formatted_address || '');
  const [addressData, setAddressData] = useState<AddressData | null>(initialAddress || null);
  const hasInitialized = useRef(false);

  const isValid = address.length >= 3 && addressData !== null;
  const showAddressHint = address.length >= 3 && addressData === null;

  // Update local state when initialAddress prop changes (only on first mount)
  React.useEffect(() => {
    if (initialAddress && !hasInitialized.current) {
      setAddress(initialAddress.formatted_address);
      setAddressData(initialAddress);
      hasInitialized.current = true;
    }
  }, [initialAddress]);

  // Diagnostic logging for validation state (CT-5853)
  React.useEffect(() => {
    logger.debug('IntroAddress validation', 'user_action', {
      addressLength: address.length,
      hasAddressData: addressData !== null,
      isValid,
      showAddressHint,
    });
    onValidationChange(isValid);
    onDataChange(addressData);
  }, [isValid, addressData, address.length, showAddressHint]);

  const handlePlaceSelected = (place: any) => {
    if (place.formatted_address) {
      // Remove ", USA" from the end since all addresses are in the USA
      const formattedAddress = place.formatted_address.replace(/, USA$/, '');
      
      // Update the place object's formatted_address as well
      const updatedPlace = {
        ...place,
        formatted_address: formattedAddress,
      };
      
      const newAddressData = {
        formatted_address: formattedAddress,
        place: updatedPlace,
      };
      logger.debug('IntroAddress: Place selected', 'user_action', {formattedAddress});
      setAddress(formattedAddress);
      setAddressData(newAddressData);
    }
  };

  const handleAddressChange = (newAddress: string) => {
    setAddress(newAddress);
    // Clear addressData if user manually types (different from current selection)
    if (addressData && newAddress !== addressData.formatted_address) {
      logger.debug('IntroAddress: Manual typing cleared addressData', 'user_action', {
        previousAddress: addressData.formatted_address,
        newTypedAddress: newAddress,
      });
      setAddressData(null);
    }
  };

  return (
    <VStack
      flex={1}
      spacing={6}
      px={4}
      pt={4}
      pb={4}
      align="stretch"
      justify="flex-start"
      overflow="auto">
      {/* Title */}
      <Box>
        <TextG24 fontSize="26px" fontWeight="700" lineHeight="34px" color="#262626">
          Community within
        </TextG24>
        <TextG24 fontSize="26px" fontWeight="700" lineHeight="34px" color="#C9136B">
          walking distance
        </TextG24>
      </Box>

      {/* Description */}
      <TextG16 lineHeight="24px" color="#636363" fontWeight="500">
        Tell us where you live, and we'll connect you with people and experiences{' '}
        <TextG16 color="#262626" as="span" fontWeight="500">
          right around the corner.
        </TextG16>
      </TextG16>

      {/* Address Input */}
      <Box>
        <GooglePlacesAutocomplete
          placeholder="Street address"
          value={address}
          onPlaceSelected={handlePlaceSelected}
          onAddressChange={handleAddressChange}
          size="lg"
          borderRadius="12px"
          border="2px solid"
          borderColor={theme.colors.gray[900]}
          _focus={{
            borderColor: theme.colors.gray[900],
            boxShadow: 'none',
          }}
          fontSize="16px"
          py={6}
        />
        {/* Hint when user types manually without selecting from dropdown (CT-5853) */}
        {showAddressHint && (
          <HStack mt={2} spacing={1} align="center">
            <Icon
              iconName="fi fi-rr-info"
              style={{color: '#636363', fontSize: '14px'}}
            />
            <TextG14 color="#636363" fontWeight="500">
              Select an address from the suggestions above
            </TextG14>
          </HStack>
        )}
      </Box>

      {/* Privacy Notice */}
      <HStack spacing={2} align="flex-start" p={3} borderRadius="8px">
        <Box
          bg={'#D1F4D0'}
          borderRadius="8px"
          w="44px"
          h="44px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}>
          <Icon
            iconName="fi fi-sr-shield-check"
            style={{color: '#048400', fontSize: '26px', width: '26px', height: '26px'}}
          />
        </Box>
        <TextG14 fontWeight="500" lineHeight="22px" color={'#262626'}>
          Your address is private! No one in the app will see it.
        </TextG14>
      </HStack>
    </VStack>
  );
};

export default IntroAddress;
